import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import {
  Network,
  Shield,
  Route,
  Globe,
  ArrowUpRight,
  ArrowDownLeft,
  Box,
  Server,
  Layers,
  GitBranch,
  Cpu,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import type { TrafficHop, HopType, MatchedRule, MatchedRoute } from './types';

// =============================================================================
// NODE DATA TYPES
// =============================================================================

export interface TrafficNodeData extends TrafficHop {
  label: string;
  isFirst?: boolean;
  isLast?: boolean;
}

// =============================================================================
// STYLING CONSTANTS
// =============================================================================

const STATUS_STYLES = {
  allowed: {
    border: 'border-emerald-400',
    bg: 'bg-emerald-50',
    icon: 'text-emerald-600',
    badge: 'bg-emerald-500',
  },
  denied: {
    border: 'border-red-400',
    bg: 'bg-red-50',
    icon: 'text-red-600',
    badge: 'bg-red-500',
  },
  unknown: {
    border: 'border-slate-300',
    bg: 'bg-slate-50',
    icon: 'text-slate-500',
    badge: 'bg-slate-500',
  },
};

const HOP_ICONS: Record<HopType, React.ReactNode> = {
  ENI: <Network className="w-5 h-5" />,
  SUBNET: <Layers className="w-5 h-5" />,
  NACL: <Shield className="w-5 h-5" />,
  SG: <Shield className="w-5 h-5" />,
  ROUTE_TABLE: <Route className="w-5 h-5" />,
  NAT_GW: <ArrowUpRight className="w-5 h-5" />,
  IGW: <Globe className="w-5 h-5" />,
  TGW: <GitBranch className="w-5 h-5" />,
  VPC_PEERING: <GitBranch className="w-5 h-5" />,
  VPC: <Box className="w-5 h-5" />,
  EXTERNAL: <Globe className="w-5 h-5" />,
  VGW: <Server className="w-5 h-5" />,
  VPCE: <Cpu className="w-5 h-5" />,
};

const HOP_LABELS: Record<HopType, string> = {
  ENI: 'ENI',
  SUBNET: 'Subnet',
  NACL: 'NACL',
  SG: 'Security Group',
  ROUTE_TABLE: 'Route Table',
  NAT_GW: 'NAT Gateway',
  IGW: 'Internet Gateway',
  TGW: 'Transit Gateway',
  VPC_PEERING: 'VPC Peering',
  VPC: 'VPC',
  EXTERNAL: 'External',
  VGW: 'VPN Gateway',
  VPCE: 'VPC Endpoint',
};

// =============================================================================
// BASE NODE COMPONENT
// =============================================================================

export const TrafficNode: React.FC<NodeProps<TrafficNodeData>> = ({ data }) => {
  const styles = STATUS_STYLES[data.status];
  const icon = HOP_ICONS[data.type];
  const typeLabel = HOP_LABELS[data.type];

  const StatusIcon =
    data.status === 'allowed' ? CheckCircle2 :
    data.status === 'denied' ? XCircle : AlertCircle;

  return (
    <div
      className={`
        relative min-w-[180px] max-w-[280px] rounded-xl border-2 shadow-lg
        ${styles.border} ${styles.bg}
        transition-all duration-200 hover:shadow-xl
      `}
    >
      {/* Handles */}
      {!data.isFirst && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white"
        />
      )}
      {!data.isLast && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white"
        />
      )}

      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-200/50">
        <div className="flex items-center gap-2">
          <div className={`${styles.icon}`}>{icon}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
              {typeLabel}
            </div>
            <div className="text-sm font-semibold text-slate-800 truncate" title={data.name}>
              {data.name}
            </div>
          </div>
          <div className={`${styles.badge} rounded-full p-1`}>
            <StatusIcon className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="px-3 py-2 text-xs space-y-1">
        {/* IP/Port info */}
        {data.details.ip && (
          <div className="flex items-center gap-1.5 text-slate-600">
            <span className="font-medium text-slate-500">IP:</span>
            <span className="font-mono">{data.details.ip}</span>
            {data.details.port && data.details.port > 0 && (
              <span className="font-mono">:{data.details.port}</span>
            )}
          </div>
        )}

        {/* CIDR info */}
        {data.details.cidr && (
          <div className="flex items-center gap-1.5 text-slate-600">
            <span className="font-medium text-slate-500">CIDR:</span>
            <span className="font-mono">{data.details.cidr}</span>
          </div>
        )}

        {/* NAT Gateway public IP */}
        {data.details.publicIp && (
          <div className="flex items-center gap-1.5 text-slate-600">
            <span className="font-medium text-slate-500">Public IP:</span>
            <span className="font-mono">{data.details.publicIp}</span>
          </div>
        )}

        {/* Matched Rule (for NACL/SG) */}
        {data.details.matchedRule && (
          <MatchedRuleDisplay rule={data.details.matchedRule} />
        )}

        {/* Matched Route (for Route Table) */}
        {data.details.matchedRoute && (
          <MatchedRouteDisplay route={data.details.matchedRoute} />
        )}

        {/* Resource ID (subtle) */}
        <div className="pt-1 text-[10px] text-slate-400 font-mono truncate" title={data.id}>
          {data.id}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MATCHED RULE DISPLAY
// =============================================================================

const MatchedRuleDisplay: React.FC<{ rule: MatchedRule }> = ({ rule }) => {
  const isAllow = rule.action === 'allow';

  return (
    <div
      className={`
        mt-1 p-2 rounded-lg border text-[10px]
        ${isAllow ? 'bg-emerald-50/50 border-emerald-200' : 'bg-red-50/50 border-red-200'}
      `}
    >
      <div className="font-semibold text-slate-600 uppercase tracking-wide mb-1">
        Rule Match
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
        {rule.ruleNumber !== undefined && (
          <>
            <span className="text-slate-500">Rule #:</span>
            <span className="font-mono">{rule.ruleNumber}</span>
          </>
        )}
        <span className="text-slate-500">Action:</span>
        <span className={`font-semibold ${isAllow ? 'text-emerald-600' : 'text-red-600'}`}>
          {rule.action.toUpperCase()}
        </span>
        <span className="text-slate-500">Protocol:</span>
        <span className="font-mono">{rule.protocol}</span>
        <span className="text-slate-500">Port:</span>
        <span className="font-mono">{rule.portRange}</span>
        <span className="text-slate-500">CIDR:</span>
        <span className="font-mono truncate" title={rule.cidr}>{rule.cidr}</span>
      </div>
    </div>
  );
};

// =============================================================================
// MATCHED ROUTE DISPLAY
// =============================================================================

const MatchedRouteDisplay: React.FC<{ route: MatchedRoute }> = ({ route }) => {
  return (
    <div className="mt-1 p-2 rounded-lg border bg-blue-50/50 border-blue-200 text-[10px]">
      <div className="font-semibold text-slate-600 uppercase tracking-wide mb-1">
        Route Match
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
        <span className="text-slate-500">Dest:</span>
        <span className="font-mono">{route.destination}</span>
        <span className="text-slate-500">Target:</span>
        <span className="font-mono truncate" title={route.target}>
          {route.target === 'local' ? 'local (VPC)' : route.target}
        </span>
        <span className="text-slate-500">Type:</span>
        <span className="font-mono uppercase">{route.targetType}</span>
      </div>
    </div>
  );
};

// =============================================================================
// DIRECTION BADGE
// =============================================================================

export const DirectionBadge: React.FC<{ direction: 'inbound' | 'outbound' }> = ({ direction }) => {
  return (
    <div
      className={`
        inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold
        ${direction === 'inbound'
          ? 'bg-blue-100 text-blue-800'
          : 'bg-violet-100 text-violet-800'}
      `}
    >
      {direction === 'inbound' ? (
        <>
          <ArrowDownLeft className="w-3.5 h-3.5" />
          Inbound
        </>
      ) : (
        <>
          <ArrowUpRight className="w-3.5 h-3.5" />
          Outbound
        </>
      )}
    </div>
  );
};

// =============================================================================
// CONNECTION TYPE BADGE (Stateful Detection)
// =============================================================================

interface ConnectionTypeBadgeProps {
  type?: 'initiator' | 'response' | 'unknown';
  isStatefulAllowed?: boolean;
  hasRelated?: boolean;
}

export const ConnectionTypeBadge: React.FC<ConnectionTypeBadgeProps> = ({ 
  type, 
  isStatefulAllowed,
  hasRelated 
}) => {
  if (!type || type === 'unknown') {
    if (!hasRelated) {
      return (
        <span className="text-xs text-slate-400 italic">
          One-way
        </span>
      );
    }
    return null;
  }

  if (type === 'initiator') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
        <ArrowUpRight className="w-3 h-3" />
        Initiator
      </div>
    );
  }

  // Response
  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-800">
        <ArrowDownLeft className="w-3 h-3" />
        Response
      </div>
      {isStatefulAllowed && (
        <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5">
          <CheckCircle2 className="w-2.5 h-2.5" />
          Stateful
        </span>
      )}
    </div>
  );
};

// =============================================================================
// NODE TYPES EXPORT
// =============================================================================

export const trafficNodeTypes = {
  trafficHop: TrafficNode,
};

